<script setup lang="ts">
import { capitalize, defineEmits } from 'vue';
import BookActionButtons from '@/components/bookshelf/BookActionButtons.vue';
import BookCover from '@/components/bookshelf/BookCover.vue';
import { useLocale } from '@/plugins/translation';
import { BookWithRelatedContents } from '@ilteducation/videos';
import bookDeepLinkUrl from '@/composables/use-book-deeplink';
import { DrawerBottom } from '@ilteducation/components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

defineProps<{
  book: BookWithRelatedContents;
  bookInAllLanguages: BookWithRelatedContents[];
}>();
const emit = defineEmits<{
  close: [];
  selectBook: [book: BookWithRelatedContents];
}>();
const expanded = defineModel({
  default: false,
});
const { t, l } = useLocale();

const bookLanguage = (book: BookWithRelatedContents) =>
  l.languages[(book.languages.secondaryLanguage ?? book.languages.mainLanguage) as keyof typeof l.languages];

const handleBookSelect = (book: BookWithRelatedContents | null) => {
  if (!book) return;
  emit('selectBook', book);
};
</script>

<template>
  <DrawerBottom
    :model-value="expanded"
    @close="expanded = false"
    :dialog-label="book.title"
    color="surface-container-lowest"
    :extra-classes="['rounded-t-xl', 'px-4', 'py-0']"
  >
    <v-container class="d-flex flex-column gr-4 px-0 overflow-y-auto">
      <BookCover
        class="d-flex justify-center justify-sm-start align-self-center"
        :max-width="150"
        playable
        :book="book"
        :zoom-on-hover="false"
      />
      <BookActionButtons class="mt-2" :book="book" />
      <div class="d-flex flex-column gr-3 px-4">
        <div v-if="book.authors">
          <h6>{{ t(l.library.author) }}</h6>
          <p>
            {{
              book.authors
                .filter((a) => a.lastName)
                .map((author) => `${author.firstName} ${author.lastName}`)
                .join(', ')
            }}
          </p>
        </div>
        <div v-if="book.publisher.name">
          <h6>{{ t(l.library.publisher) }}</h6>
          <p>{{ book.publisher.name }}</p>
        </div>
      </div>
      <v-select
        v-if="bookInAllLanguages.length > 1"
        density="comfortable"
        variant="outlined"
        class="mb-1"
        rounded="md"
        item-value="id"
        :item-title="(item) => capitalize(t(bookLanguage(item) as string))"
        :items="bookInAllLanguages"
        hide-details
        return-object
        :model-value="book"
        @update:modelValue="handleBookSelect"
      >
        <template #prepend-inner>
          <FontAwesomeIcon size="sm" class="mr-2" icon="fa-regular fa-globe"></FontAwesomeIcon>
        </template>
      </v-select>
      <h1 class="book-title">{{ book.title }}</h1>
      <p class="book-description" v-html="book.description"></p>
      <div v-if="book.items" class="d-flex flex-column">
        <h5 id="title_related_content" class="text-h6">{{ t(l.library.related_content) }}</h5>
        <v-list class="w-100 bg-transparent" aria-labelledby="title_related_content">
          <v-list-item
            v-for="i in book.items"
            :key="i.id"
            target="_blank"
            :href="bookDeepLinkUrl(book, i)"
            :title="(i.metadata?.title || '') as string"
          >
            <template #prepend>
              <FontAwesomeIcon class="mr-2" icon="fa-regular fa-link"></FontAwesomeIcon>
            </template>
            <template #append>
              <FontAwesomeIcon icon="fa-regular fa-chevron-right"></FontAwesomeIcon>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </v-container>
  </DrawerBottom>
</template>

<style scoped lang="scss">
.book-title {
  font-size: 1.25rem;
}
:deep(ul),
:deep(ol) {
  padding-left: 1.5em;
}

:deep(.book-description) span,
:deep(.book-description) p {
  font-size: 1rem !important;
  font-weight: 400;
}
</style>
